import React, { useEffect, useRef } from 'react';

import { usePaymentErrorContext } from '../../contexts/payment-error-context';
import { useAvailablePaymentMethods } from '../../hooks/use-available-payment-methods';
import { usePaymentMethodManager } from '../../hooks/use-payment-method-manager';
import { PaymentMethod, PaymentMethodManagerProps } from '../../types';
import { isDirectPaymentMethod } from '../../utils/payment-methods';
import { PaymentMethodView } from './payment-method-view';

const PaymentMethodManagerCore: React.FC<PaymentMethodManagerProps> = ({
    paymentProcessor
}) => {
    const manager = usePaymentMethodManager(paymentProcessor);
    const {
        methods: availablePaymentMethods,
        isLoading: isLoadingAvailableMethods,
        hasLoaded: hasLoadedAvailableMethods,
        hasError: hasAvailableMethodsError
    } = useAvailablePaymentMethods(paymentProcessor);

    const { state: errorState } = usePaymentErrorContext();
    const hasErrors = errorState.errors.length > 0;

    const { currentView } = manager;
    const isDirectRoute =
        !manager.isLoaded || manager.savedMethods.length === 0;
    const hasCompletedAddingMethodRef = useRef(false);

    const isManualAddNewRef = useRef(false);
    const isAutoNavigatingRef = useRef(false);

    useEffect(() => {
        if (
            isAutoNavigatingRef.current ||
            hasErrors ||
            manager.isLoading ||
            !hasLoadedAvailableMethods ||
            isLoadingAvailableMethods ||
            hasAvailableMethodsError ||
            hasCompletedAddingMethodRef.current
        ) {
            return;
        }

        const availableMethods = availablePaymentMethods.flatMap(
            (provider) =>
                provider.paymentMethods?.map(
                    (method) => method.paymentMethod
                ) || []
        );
        const uniqueMethods = Array.from(new Set(availableMethods));
        const nonDirectMethods = uniqueMethods.filter(
            (method) => !isDirectPaymentMethod(method)
        );

        const autoNavigate = async (methodType: PaymentMethod) => {
            isAutoNavigatingRef.current = true;
            try {
                const success = await manager.handleTypeSelect(methodType);
                if (!success) {
                    manager.handleEvent({ type: 'CANCEL_ADD_NEW' });
                }
            } finally {
                isAutoNavigatingRef.current = false;
            }
        };

        if (
            manager.currentView === 'main' &&
            manager.savedMethods.length === 0
        ) {
            if (nonDirectMethods.length === 1 && uniqueMethods.length === 1) {
                void autoNavigate(nonDirectMethods[0]);
                return;
            }
            if (uniqueMethods.length > 0) {
                manager.handleEvent({ type: 'START_ADD_NEW' });
                return;
            }
        }

        if (
            manager.currentView === 'selecting-type' &&
            isManualAddNewRef.current &&
            uniqueMethods.length === 1
        ) {
            isManualAddNewRef.current = false;
            void autoNavigate(uniqueMethods[0]);
            return;
        }
    }, [
        hasErrors,
        manager.currentView,
        manager.isLoading,
        manager.savedMethods.length,
        manager.handleTypeSelect,
        manager.handleEvent,
        hasLoadedAvailableMethods,
        isLoadingAvailableMethods,
        hasAvailableMethodsError,
        availablePaymentMethods
    ]);

    useEffect(() => {
        if (hasCompletedAddingMethodRef.current && manager.isLoaded) {
            hasCompletedAddingMethodRef.current = false;
        }
    }, [manager.isLoaded, manager.savedMethods.length]);

    const handlers = {
        handleMethodSelect: async (methodId: string) => {
            if (methodId === 'add-new') {
                isManualAddNewRef.current = true;
            }
            await manager.handleMethodSelect(methodId);
        },
        handleStartAddNew: () => {
            isManualAddNewRef.current = true;
            manager.handleEvent({ type: 'START_ADD_NEW' });
        },
        handleTypeSelect: manager.handleTypeSelect,
        handleCancelAddNew: () => {
            isManualAddNewRef.current = false;
            manager.handleEvent({ type: 'CANCEL_ADD_NEW' });
        },
        handleCancelToTypeSelection: () => {
            isManualAddNewRef.current = false;
            manager.handleEvent({
                type: 'CANCEL_TO_TYPE_SELECTION'
            });
        },
        handleCancelToMain: () => {
            isManualAddNewRef.current = false;
            manager.handleEvent({ type: 'CANCEL_TO_MAIN' });
        },
        handlePaymentMethodAdded: () => {
            hasCompletedAddingMethodRef.current = true;
            return manager.handlePaymentMethodAdded();
        }
    };

    return (
        <PaymentMethodView
            currentView={currentView}
            selectedMethod={manager.selectedMethod}
            currentMethodType={manager.currentMethodType}
            processingMethodId={manager.processingMethodId}
            isPaymentLoading={manager.isPaymentLoading}
            isLoadingAddNew={manager.isLoadingAddNew}
            loadingMethodType={manager.loadingMethodType}
            savedMethods={manager.savedMethods}
            isLoading={manager.isLoading}
            paymentProcessor={paymentProcessor}
            availablePaymentMethods={availablePaymentMethods}
            isLoadingAvailableMethods={isLoadingAvailableMethods}
            hasLoadedAvailableMethods={hasLoadedAvailableMethods}
            hasAvailableMethodsError={hasAvailableMethodsError}
            handlers={handlers}
            isDirectRoute={isDirectRoute}
        />
    );
};

export const PaymentMethodManager = PaymentMethodManagerCore;
